import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IBodyMapAlert } from '../../../models/bodyMapAlert';
import { IInjury } from '../../../models/injury';
import { ZONE_LABELS, TYPE_LABELS, IMPACT_LABELS, getIntensityColor } from '../../../shared/bodyMap/bodyMapScale';
import { CustomBadge } from '../../../shared/CustomBadge/CustomBadge';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import styles from './BodyMapTimeline.module.scss';

/** Etichetta italiana della zona (fallback: chiave grezza leggibile). */
const zoneLabel = (zone: string): string => ZONE_LABELS[zone] ?? zone.replace(/_/g, ' ');

interface ComplaintItem {
  zone: string;
  intensity: number;
  type?: string;
  impact?: string;
  note?: string;
}

interface BodyMapComplaintDoc {
  _id: string;
  date: string;
  complaints: ComplaintItem[];
}

interface BodyMapTimelineProps {
  athleteId: string;
  injuries: IInjury[];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' });
}

export const BodyMapTimeline: FC<BodyMapTimelineProps> = ({ athleteId, injuries }) => {
  const endpoints = useEndpoints();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: docs, isLoading, isError, refetch } = useQuery<BodyMapComplaintDoc[]>({
    queryKey: ['body-map-athlete', athleteId],
    queryFn: () => endpoints.getBodyMapAthlete(athleteId).then((r) => r.data as BodyMapComplaintDoc[]),
    staleTime: 1000 * 60 * 5,
  });

  const { data: bodyMapAlerts } = useQuery<IBodyMapAlert[]>({
    queryKey: ['body-map-alerts-athlete', athleteId],
    queryFn: () =>
      endpoints.getBodyMapAlerts({ athleteId, limit: 100 }).then((r) => r.data as IBodyMapAlert[]),
    staleTime: 1000 * 60 * 5,
  });

  const alertsByComplaintId = new Map<string, IBodyMapAlert[]>();
  for (const alert of bodyMapAlerts ?? []) {
    const arr = alertsByComplaintId.get(alert.bodyMapComplaintId) ?? [];
    arr.push(alert);
    alertsByComplaintId.set(alert.bodyMapComplaintId, arr);
  }

  const injuryByComplaintId = new Map<string, IInjury>();
  for (const injury of injuries) {
    if (injury.sourceBodyMapComplaintId) {
      injuryByComplaintId.set(injury.sourceBodyMapComplaintId, injury);
    }
  }

  const rows = (docs ?? []).slice(0, 30);

  return (
    <div>
      <h4 className={styles.heading}>Segnalazioni fisiche (body map)</h4>

      {isLoading ? (
        <div className={styles.stateWrap}>
          <LeopartLoader size="sm" />
        </div>
      ) : isError ? (
        <div className={styles.stateWrap}>
          <ChartErrorState onRetry={() => refetch()} />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyText}>
          Nessuna segnalazione body map negli ultimi periodi.
        </div>
      ) : (
        <div className={styles.list}>
          {rows.map((doc) => {
            const isExpanded = expandedId === doc._id;
            const alerts = alertsByComplaintId.get(doc._id) ?? [];
            const linkedInjury = injuryByComplaintId.get(doc._id);
            const hasNote = doc.complaints.some((c) => c.note);

            return (
              <div
                key={doc._id}
                className={styles.row}
                onClick={() => setExpandedId(isExpanded ? null : doc._id)}
              >
                {/* Dot sulla timeline */}
                <div className={styles.rowDot} />

                {/* Riga principale */}
                <div className={styles.rowMain}>
                  <span className={styles.rowDate}>{formatDate(doc.date)}</span>

                  {/* Pallini colorati per zona+intensità */}
                  <div className={styles.chipsRow}>
                    {doc.complaints.map((c, i) => (
                      <span
                        key={i}
                        title={`${zoneLabel(c.zone)} — intensità ${c.intensity}`}
                        className={styles.complaintChip}
                        style={{ background: getIntensityColor(c.intensity) }}
                      >
                        {zoneLabel(c.zone)}
                        <span className={styles.complaintChipIntensity}>{c.intensity}</span>
                      </span>
                    ))}
                  </div>

                  {/* Note Q4 */}
                  {hasNote && (
                    <span className={styles.noteText}>
                      {doc.complaints.filter((c) => c.note).map((c) => c.note).join(' · ')}
                    </span>
                  )}

                  {/* Badge alert generato */}
                  {alerts.length > 0 && (
                    <span className={styles.alertBadge}>{alerts.length} alert</span>
                  )}

                  {/* Badge → Infortunio (Fase C) */}
                  {linkedInjury && (
                    <span
                      onClick={(e) => { e.stopPropagation(); }}
                      title={`Infortunio: ${linkedInjury.bodyZone?.replace(/-/g, ' ')} — ${linkedInjury.injuryDate ? new Date(linkedInjury.injuryDate).toLocaleDateString('it-IT') : ''}`}
                    >
                      <CustomBadge label="→ Infortunio" color="lightred" />
                    </span>
                  )}

                  <span className={styles.expandToggle}>
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </div>

                {/* Espansione: tipo Q2 + impatto Q3 */}
                {isExpanded && (
                  <div className={styles.expandedPanel}>
                    {doc.complaints.map((c, i) => (
                      <div key={i} className={styles.expandedItem}>
                        <span className={styles.expandedZone}>{zoneLabel(c.zone)}</span>
                        {' · '}
                        <span>Intensità {c.intensity}</span>
                        {c.type && <span> · {TYPE_LABELS[c.type] ?? c.type}</span>}
                        {c.impact && <span className={styles.expandedMuted}> · {IMPACT_LABELS[c.impact] ?? c.impact}</span>}
                        {c.note && <span className={styles.expandedNote}> — "{c.note}"</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BodyMapTimeline;
